import { useEffect, useMemo, useRef, useState } from "react";

import {
  DEG,
  KIND_COLOR,
  PLAN_MARGIN as MARGIN,
  cameraBlockers,
  viewPath,
} from "../lib/planogram";
import type { Planogram } from "../types";

/** The floor plan as drawn on the branch PC, read-only: walls, fixtures and
 *  every camera's view region clipped by what blocks it. The editor
 *  (`PlanogramEditor`) and the heatmap own the same geometry; this one draws
 *  nothing but the plan, for a console that cannot change it — the hub. */

const LABEL = "pointer-events-none select-none fill-text font-sans";

export function PlanogramView({ plan }: { plan: Planogram }) {
  const svgRef = useRef<SVGSVGElement>(null);
  // Metres per screen pixel — keeps text a constant on-screen size.
  const [k, setK] = useState(0.02);

  const { width, height, fixtures, walls, cameras } = plan;

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const update = () => {
      const w = svg.getBoundingClientRect().width;
      if (w > 0) setK((width + 2 * MARGIN) / w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(svg);
    return () => ro.disconnect();
  }, [width]);

  const views = useMemo(
    () =>
      new Map(
        cameras.map((c) => [c.id, viewPath(c, cameraBlockers(c, walls ?? [], fixtures))]),
      ),
    [cameras, walls, fixtures],
  );

  return (
    <div className="max-w-[960px] overflow-hidden rounded-card border border-border bg-surface leading-[0]">
      <svg
        ref={svgRef}
        className="block h-auto w-full"
        viewBox={`${-MARGIN} ${-MARGIN} ${width + 2 * MARGIN} ${height + 2 * MARGIN}`}
      >
        <rect x={0} y={0} width={width} height={height} fill="var(--bg)" />
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="none"
          stroke="var(--text)"
          strokeWidth={3}
          vectorEffect="non-scaling-stroke"
          opacity={0.75}
        />

        {fixtures.map((f) => (
          <g key={f.id}>
            <rect
              x={f.x}
              y={f.y}
              width={f.w}
              height={f.h}
              fill={KIND_COLOR[f.kind]}
              fillOpacity={f.kind === "zone" ? 0.04 : 0.14}
              stroke={KIND_COLOR[f.kind]}
              strokeOpacity={0.6}
              strokeWidth={1.25}
              strokeDasharray={f.kind === "zone" ? `${6 * k} ${4 * k}` : undefined}
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={f.x + f.w / 2}
              y={f.y + f.h / 2}
              textAnchor="middle"
              dominantBaseline="central"
              className={LABEL}
              fontSize={10 * k}
              opacity={0.75}
            >
              {f.label || f.kind}
            </text>
          </g>
        ))}

        {(walls ?? []).map((w) => (
          <line
            key={w.id}
            x1={w.x1}
            y1={w.y1}
            x2={w.x2}
            y2={w.y2}
            stroke="var(--text)"
            strokeWidth={4}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            opacity={0.85}
          />
        ))}

        {cameras.map((c) => {
          // Entrance cameras are the ones that count footfall; the rest only
          // feed the heatmap, so they read as secondary here too.
          const color = c.entry ? "var(--accent)" : "var(--muted)";
          return (
            <g key={c.id}>
              <path
                d={views.get(c.id) ?? ""}
                fill={color}
                fillOpacity={c.entry ? 0.12 : 0.07}
                stroke={color}
                strokeOpacity={0.8}
                strokeWidth={1.25}
                strokeDasharray={c.entry ? undefined : `${5 * k} ${4 * k}`}
                vectorEffect="non-scaling-stroke"
              />
              <circle
                cx={c.x}
                cy={c.y}
                r={6.5 * k}
                fill={color}
                stroke="var(--bg)"
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
              />
              {/* aim tick so orientation reads even on tiny cones */}
              <line
                x1={c.x}
                y1={c.y}
                x2={c.x + Math.cos(c.rotation * DEG) * Math.min(c.range, 14 * k)}
                y2={c.y + Math.sin(c.rotation * DEG) * Math.min(c.range, 14 * k)}
                stroke="var(--bg)"
                strokeWidth={1.5}
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={c.x}
                y={c.y - 12 * k}
                textAnchor="middle"
                className={LABEL}
                fontSize={11 * k}
                fontWeight={600}
              >
                {c.label || c.id}
                {c.entry ? " · entrance" : ""}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
